import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import '../ui/Form.scss';
import usStates from './usStates.js';
import { trackCustomEvent } from "../../utils/eventsTracking/trackCustomEvent.js";
import FloatingLabelInput from "../forms/FloatingLabelInput.jsx";
import { useIsPartnerAffiliate } from "../../hooks/useIsPartnerAffiliate.js";
import { captureCartForRecovery } from "../../utils/cartRecovery.js";

const CheckoutFormSimplified = ({
  comment,
  affiliate,
  setComment,
  isMobile,
  setClientSecret,
  getStripeIntent,
  formikRef,
  setCheckoutError,
  setBillingDetails,
  setIsFormCompleted,
}) => {
  const hasTrackedCheckoutStart = useRef(false);
  const isPartnerAffiliate = useIsPartnerAffiliate();
  const isDev = import.meta.env.DEV;


  // Validation Schema
  const validationSchema = Yup.object({
    firstName: Yup.string().required("First name is required"),
    surname: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email address").required("E-mail is required"),
    phone: Yup.string().required("Phone number is required"),
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    address: Yup.string().required("Address is required"),
    postalCode: Yup.string().required("Zip code is required"),
  });

  // Initial Form Values
  const initialValues = {
    firstName: "",
    surname: "",
    email: "",
    phone: "",
    state: "",
    city: "",
    address: "",
    address2: "",
    postalCode: "",
  };

  const handleCheckoutStartTrack = () => {
    if (hasTrackedCheckoutStart.current) return;
    hasTrackedCheckoutStart.current = true;
    trackCustomEvent('checkout_start', {}, affiliate?.code || null);
  };

  const handleSubmit = async (values) => {
    setCheckoutError([]);
    try {
      await getStripeIntent(values);
    } catch (err) {
      setCheckoutError([err.message || "Failed to continue to payment."]);
    }
  };


  return (
    <Formik innerRef={formikRef} initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ isSubmitting, isValid, dirty, values, errors, touched }) => {
        const isFormComplete = isValid && dirty;

        useEffect(() => {
          setBillingDetails(values);
          setIsFormCompleted(isFormComplete);
        }, [values, setClientSecret, setBillingDetails, isFormComplete, setIsFormCompleted]);

        return (
          <Form className="form-container flex flex-col gap-5 md:gap-10" id="checkout-form">
            <div className="flex flex-col gap-3 w-full px-[20px]">
              <h5 className={`form-title body-heading text-left color-swap ${isMobile ? 'mt-10' : ''}`}>Contact</h5>
              <FloatingLabelInput
                name="email"
                label="E-mail *"
                type="email"
                onFocus={handleCheckoutStartTrack}
                onBlur={async (e) => {
                  const email = e.target.value?.trim();
                  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

                  if (isValid && !isDev) {
                    captureCartForRecovery(email, affiliate?.code || null, isPartnerAffiliate);
                  }
                }}
              />
              <FloatingLabelInput
                name="phone"
                label="Phone Number *"
                type="tel"
                onFocus={handleCheckoutStartTrack}
              />
            </div>

            <div className="fade-step flex flex-col gap-3 w-full px-[20px]">
              <h5 className={`form-title body-heading text-left color-swap ${isMobile ? 'mt-5' : ''}`}>Delivery</h5>
              <div className="flex flex-col md:flex-row gap-4">
                <FloatingLabelInput
                  name="firstName"
                  label="First Name *"
                  type="text"
                  onFocus={handleCheckoutStartTrack}
                />
                <FloatingLabelInput
                  name="surname"
                  label="Last Name *"
                  type="text"
                  onFocus={handleCheckoutStartTrack}
                />
              </div>
              <FloatingLabelInput
                name="address"
                label="Address *"
                type="text"
                onFocus={handleCheckoutStartTrack}
              />
              <FloatingLabelInput
                name="address2"
                label="Apartment, suite, etc. (optional)"
                type="text"
                onFocus={handleCheckoutStartTrack}
              />
              <div className="flex flex-col md:flex-row gap-4">
                <FloatingLabelInput
                  name="city"
                  label="City *"
                  type="text"
                  onFocus={handleCheckoutStartTrack}
                />
                <FloatingLabelInput
                  name="state"
                  label="State *"
                  as="select"
                >
                  <option value="">Select State</option>
                  {usStates.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </FloatingLabelInput>
                <FloatingLabelInput
                  name="postalCode"
                  label="Zip Code *"
                  type="text"
                  onFocus={handleCheckoutStartTrack}
                />
              </div>
            </div>

            <div className="px-[20px]">
              <FloatingLabelInput
                label="Special Instructions for Seller"
                as="textarea"
                name="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </Form>
        )
      }}
    </Formik >
  );
};

CheckoutFormSimplified.propTypes = {
  comment: PropTypes.string,
  affiliate: PropTypes.object,
  setComment: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  setClientSecret: PropTypes.func.isRequired,
  getStripeIntent: PropTypes.func.isRequired,
  formikRef: PropTypes.object,
  setCheckoutError: PropTypes.func.isRequired,
  setBillingDetails: PropTypes.func.isRequired,
  setIsFormCompleted: PropTypes.func.isRequired
};

export default CheckoutFormSimplified;